import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Drawer, DrawerItems, Label, Select, TextInput } from 'flowbite-react';
import { Loader2, X } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import CustomButton from 'src/components/common/custom-buttom';
import { AccessRight } from 'src/routes/PermissionEnum';
import { userSchema } from 'src/schema';
import { useTMutation, useTQuery } from 'src/services/common';
const defaultValues = {
  firstName: '',
  lastName: '',
  status: 'active',
  emailAddress: '',
  roleID: '',
};

const AddEditUser = ({ initialData, handleClose }: any) => {
    
  const { mutateAsync, isPending } = useTMutation({
    url: initialData?._id ? `users/${initialData?._id}` : 'users',
    method: initialData?._id ? 'patch' : 'post',
    invalidateTag: ['users'],
  });
  const { mutateAsync: deleteTemplate, isPending: deleteIsPending } = useTMutation({
    url: `users/${initialData?._id}`,
    method: 'delete',
    invalidateTag: ['users'],
  });

  const { data, isLoading } = useTQuery({ key: ['role'], url: 'role' });

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues,
    resolver: zodResolver(userSchema),
  });

  const onSubmit = async (data: any) => {
    let response: any = await mutateAsync(data );
    if (response?.status) {
      toast.success(response?.message);
      onClose();
    } else {
      toast.error(response?.data?.message);
    }
  };
  const onDelete = async () => {
    let response: any = await deleteTemplate({});
    if (response?.status) {
      toast.success(response?.message);
      onClose();
    } else {
      toast.error(response?.data?.message);
    }
  };
  const onClose = () => {
    handleClose(false);
    reset(defaultValues);
  };



  useEffect(() => {
    if (initialData?._id) {
        reset(initialData);
    }
  }, [initialData, reset]);

  return (
    <>
      <Drawer className={`p-4 w-[60%]`} open={initialData} onClose={onClose} position="right">
        <div className="flex flex-1 justify-between cursor-pointer">
          <Label className="text-[22px]" value={initialData?._id ? 'Edit Role' : 'Add Role'} />
          <X className="rounded-full bg-red-200 p-1 cursor-pointer" onClick={onClose} />
        </div>

        <DrawerItems className="mt-5">
          <form onSubmit={handleSubmit(onSubmit)} className="w-full">
            <div className="flex flex-col gap-3">
              <div className="flex gap-2">
                <div className="flex-1">
                  <div className="mb-2 block">
                    <Label htmlFor="firstName" value="First Name" />
                  </div>
                  <TextInput
                    id="firstName"
                    type="text"
                    sizing="md"
                    className="form-control form-rounded-xl"
                    {...register('firstName')}
                  />
                  {errors.firstName && (
                    <p className="text-red-500 text-sm">{errors.firstName.message}</p>
                  )}
                </div>
                <div className="flex-1">
                  <div className="mb-2 block">
                    <Label htmlFor="lastName" value="Last Name" />
                  </div>
                  <TextInput
                    id="lastName"
                    type="text"
                    sizing="md"
                    className="form-control form-rounded-xl"
                    {...register('lastName')}
                  />
                  {errors.lastName && (
                    <p className="text-red-500 text-sm">{errors.lastName.message}</p>
                  )}
                </div>
                <div className="flex-1">
                  <div className="mb-2 block">
                    <Label htmlFor="emailAddress" value="Email Address" />
                  </div>
                  <TextInput
                    id="emailAddress"
                    type="text"
                    sizing="md"
                    className="form-control form-rounded-xl"
                    {...register('emailAddress')}
                  />
                  {errors.emailAddress && (
                    <p className="text-red-500 text-sm">{errors.emailAddress.message}</p>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                <div className="flex-1">
                  <div className="mb-2 block">
                    <Label htmlFor="roleID" value="Role" />
                  </div>
                  <Select
                    {...register('roleID')}
                    id="roleID"
                    required
                    className="focus:ring-0 border-gray-300"
                    style={{ borderRadius: '10px' }} // <-- removes border-radius
                  >
                    {(data?.data?.data || [])?.map((val) => (
                      <option value={val?._id}>{val?.name}</option>
                    ))}
                  </Select>
                  {errors.roleID && <p className="text-red-500 text-sm">{errors.roleID.message}</p>}
                </div>
                <div className="flex-1">
                  <div className="mb-2 block">
                    <Label htmlFor="status" value="Status" />
                  </div>
                  <Select
                    {...register('status')}
                    id="status"
                    required
                    className="focus:ring-0 border-gray-300"
                    style={{ borderRadius: '10px' }} // <-- removes border-radius
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </Select>

                  {errors.status && <p className="text-red-500 text-sm">{errors.status.message}</p>}
                </div>
              </div>

              <div className="flex gap-2 justify-end">
                {initialData?._id && (
                  <CustomButton accessRight={AccessRight.ISEDIT} onClick={() => onDelete()} className="bg-red-500 text-white rounded-xl">
                    {deleteIsPending ? <Loader2 className="animate-spin" /> : 'Delete'}
                  </CustomButton>
                )}

                <Button onClick={onClose} color="light" className=" rounded-xl">
                  Cancel
                </Button>
                <CustomButton type="submit"  accessRight={AccessRight.ISEDIT}  color="green" className="bg-green-400 text-white rounded-xl">
                  {isPending ? <Loader2 className="animate-spin" /> : 'Save'}
                </CustomButton>
              </div>
            </div>
          </form>
        </DrawerItems>
      </Drawer>
    </>
  );
};
export default AddEditUser;
